import * as React from 'react';
import { useNavigate } from 'react-router';
import { create } from 'zustand';
import type { Storage } from '@chess/api-storage';
import type { Brand } from '@chess/platform-web';
import { lookupPlayer, normalizeUsername, readUsername, saveUsername } from './account';
import { Preview } from './Preview';

// The landing page's own state: the username being typed and where the
// check stands. Nothing here is read by any other plugin.
type Status = 'idle' | 'checking' | 'missing' | 'failed';
interface State {
  draft: string;
  status: Status;
  message?: string;
  setDraft(draft: string): void;
  begin(): void;
  fail(status: Exclude<Status, 'idle' | 'checking'>, message: string): void;
}
const useLanding = create<State>((set) => ({
  draft: '',
  status: 'idle',
  setDraft: (draft) => set({ draft, status: 'idle', message: undefined }),
  begin: () => set({ status: 'checking', message: undefined }),
  fail: (status, message) => set({ status, message }),
}));

const features = [
  { title: 'What’s working, what isn’t', body: 'How your recent games went, where your rating is heading, and which part of the game costs you the most.' },
  { title: 'Opening, middlegame, ending', body: 'Which openings leave you better off, how often a good position slips away, and the endings you let get away.' },
  { title: 'The moves that decided it', body: 'In any game, see where the advantage changed hands and what you could have played instead.' },
];

const steps = [
  { n: '1', title: 'Import', body: 'Type your chess.com username. Your recent games come in and the engine reads them, on this device.' },
  { n: '2', title: 'Explore', body: 'See your results, openings, middlegames and endings, and where you gain or lose the most.' },
  { n: '3', title: 'Review', body: 'Open any game, find the move that decided it, and practise it as a puzzle.' },
];

export const Landing: React.FC<{ settings: Promise<Storage>; brand: Brand; onSaved?: (username: string) => Promise<void> }> = ({ settings, brand, onSaved }) => {
  const navigate = useNavigate();
  const { draft, status, message, setDraft, begin, fail } = useLanding();
  const inputId = 'landing-username';
  // The saved account, when there is one: the form starts with it, so this
  // page is also where it is changed.
  const [saved, setSaved] = React.useState('');
  React.useEffect(() => {
    let live = true;
    settings
      .then(readUsername)
      .then((username) => {
        if (!live || !username) return;
        setSaved(username);
        if (!useLanding.getState().draft) setDraft(username);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const username = normalizeUsername(draft);
    if (!username) {
      fail('missing', 'Type your chess.com username to continue.');
      return;
    }
    begin();
    const result = await lookupPlayer(username);
    if (result.kind === 'found') {
      await saveUsername(await settings, result.username);
      // The account starts over (its games, scans and bookkeeping go), then
      // its first sync: the sync screen's `force` runs one even on a day
      // already synced.
      await onSaved?.(result.username);
      navigate('/sync', { state: { from: '/', force: true } });
    } else if (result.kind === 'missing') {
      fail('missing', `There is no chess.com player called “${username}”. Check the spelling and try again.`);
    } else {
      fail('failed', `Could not reach chess.com: ${result.message}. Try again in a moment.`);
    }
  };

  return (
    <div className="landing">
      <section className="landing-hero">
        <div className="landing-copy">
          <p className="landing-eyebrow">{brand.name}</p>
          <h1 className="landing-title">{brand.tagline}</h1>
          <p className="landing-lead">
            Find out which openings work for you, where your middlegames slip away, and which endings you should have won.
            Then go back to the games, see the moves that decided them, and practise your own mistakes as puzzles until they stick.
          </p>
          <form className="landing-cta" onSubmit={submit} noValidate>
            <label className="landing-label" htmlFor={inputId}>
              Your chess.com username
            </label>
            <div className="landing-cta-row">
              <input
                id={inputId}
                className={`landing-input${status === 'missing' || status === 'failed' ? ' landing-input-error' : ''}`}
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="e.g. hikaru"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-describedby={message ? 'landing-message' : undefined}
                aria-invalid={status === 'missing' || status === 'failed'}
                disabled={status === 'checking'}
              />
              <button className="landing-button" type="submit" disabled={status === 'checking'}>
                {status === 'checking' ? 'Checking…' : `Open ${brand.name}`}
              </button>
            </div>
            {message ? (
              <p id="landing-message" className="landing-message" role="alert">
                {message}
              </p>
            ) : (
              <p className="landing-note">
                {saved ? `Signed in as ${saved}. Another username switches the app to that player’s games.` : 'No account to create. Your games stay on this device.'}
              </p>
            )}
          </form>
          </div>
        <Preview />
      </section>

      <section className="landing-features" aria-labelledby="landing-features-title">
        <h2 id="landing-features-title" className="landing-h2">What {brand.name} shows you</h2>
        <div className="landing-grid">
          {features.map((f) => (
            <article key={f.title} className="landing-feature">
              <h3 className="landing-h3">{f.title}</h3>
              <p className="landing-body">{f.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-steps" aria-labelledby="landing-steps-title">
        <h2 id="landing-steps-title" className="landing-h2">How it works</h2>
        <ol className="landing-step-list">
          {steps.map((s) => (
            <li key={s.n} className="landing-step">
              <span className="landing-step-n" aria-hidden="true">{s.n}</span>
              <div>
                <h3 className="landing-h3">{s.title}</h3>
                <p className="landing-body">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <footer className="landing-foot">
        <details className="landing-about">
          <summary>About &amp; credits</summary>
          <p className="landing-body">{brand.about.summary}</p>
          <ul className="landing-credits">
            {brand.about.credits.map((c) => (
              <li key={c.name}>
                <a href={c.url} target="_blank" rel="noreferrer">
                  {c.name}
                </a>
                <span>{c.role}</span>
                <span className="landing-credit-licence">{c.licence}</span>
              </li>
            ))}
          </ul>
        </details>
      </footer>
    </div>
  );
};
